/**
 * Page Mon Planning (formateur) : séances où je suis enseignant.
 */
import React, { useState } from 'react';
import { CalendarDays, CheckCircle2, Clock, AlertCircle, MapPin } from 'lucide-react';
import { useMesSeancesFormateurQuery } from '../../services/vieScolaireApi';

function isoJour(d: Date): string { return d.toISOString().slice(0, 10); }

export default function MonPlanning() {
  const today = new Date();
  const ya7j = new Date(today.getTime() - 7 * 86_400_000);
  const dans14j = new Date(today.getTime() + 14 * 86_400_000);
  const [depuis, setDepuis] = useState(isoJour(ya7j));
  const [jusqua, setJusqua] = useState(isoJour(dans14j));

  const { data, isFetching } = useMesSeancesFormateurQuery({ depuis, jusqua });
  const seances = data?.results || [];

  // Group par date
  const parDate = seances.reduce<Record<string, any[]>>((acc, s) => {
    acc[s.date] = acc[s.date] || [];
    acc[s.date].push(s);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarDays size={24} /> Mon planning
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Séances où vous êtes enseignant{data?.mon_nom && ` (${data.mon_nom})`}.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label className="text-xs text-gray-600 dark:text-gray-400">Du</label>
        <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
        <label className="text-xs text-gray-600 dark:text-gray-400">au</label>
        <input type="date" value={jusqua} onChange={(e) => setJusqua(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
        <span className="ml-auto text-xs text-gray-500">
          {data?.total || 0} séance(s)
        </span>
      </div>

      {!isFetching && seances.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center">
          <CalendarDays size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <div className="text-gray-600 dark:text-gray-400 text-sm">
            Aucune séance trouvée pour vous sur cette période.
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Vérifiez que le surveillant a saisi votre nom dans l'EDT exactement comme dans votre profil.
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(parDate).sort(([a], [b]) => a.localeCompare(b)).map(([d, items]) => (
          <section key={d}>
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
              {new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(s => (
                <div key={s.id} className={`bg-white dark:bg-gray-800 border rounded-xl p-3 ${
                  s.appel_fait
                    ? 'border-green-300 dark:border-green-700'
                    : 'border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-base font-bold text-gray-900 dark:text-white">{s.classe}</div>
                    {s.statut === 'tenue' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded">
                        <CheckCircle2 size={11} /> Tenue
                      </span>
                    ) : s.statut === 'annulee' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded">
                        Annulée
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded">
                        <Clock size={11} /> Prévue
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{s.matiere}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                    <div className="flex items-center gap-1">
                      <Clock size={11} /> {s.heure_debut}–{s.heure_fin}
                    </div>
                    {s.salle && (
                      <div className="flex items-center gap-1">
                        <MapPin size={11} /> {s.salle}
                      </div>
                    )}
                  </div>
                  {s.appel_fait && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs">
                      <span className="text-gray-600 dark:text-gray-400">Présents : </span>
                      <span className="font-bold text-green-600 dark:text-green-400">{s.nb_presences_enregistrees - s.nb_absents}</span>
                      <span className="text-gray-500 mx-1">•</span>
                      <span className="text-gray-600 dark:text-gray-400">Absents : </span>
                      <span className="font-bold text-red-500">{s.nb_absents}</span>
                    </div>
                  )}
                  {!s.formateur_present && s.appel_fait && (
                    <div className="mt-2 text-xs text-orange-500 flex items-center gap-1">
                      <AlertCircle size={11} /> Marqué non présent
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
